import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHits, useRefinementList, useSearchBox } from 'react-instantsearch'
import { funnel, isEmpty } from 'remeda'
import { useEventListener } from 'usehooks-ts'
import type { FormattedVariantProps } from '@components/AlgoliaSearch/types'
import { formatAlgoliaHit } from '@components/AlgoliaSearch/utils'
import { ApparelProduct } from '@components/ApparelProduct/ApparelProduct'
import ApparelProductSkeleton from '@components/ApparelProductSkeleton/ApparelProductSkeleton'
import { useRecentProducts } from '@velasca/hooks/use-recent-products'
import { ArrowIcon } from '@velasca/icons/arrow'
import { CloseIcon } from '@velasca/icons/close'
import { SearchIcon } from '@velasca/icons/search'
import type { MinisearchData } from '@velasca/types'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import {
  generateSearchUrl,
  type Refinement,
} from '@velasca/utils/generate-search-url'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { CategoryTile } from './CategoryTile'
import { CollectionSearchResultsSkeleton } from './CollectionSearchResultsSkeleton'
import { EditorialCardsSkeleton } from './EditorialCardSkeleton'
import * as S from './Minisearch.styles'
import { ProductSearchResultsSkeleton } from './ProductSearchResultsSkeleton'
import { SuggestionsSkeleton } from './SuggestionsSkeleton'

export const MinisearchContent = ({
  data,
  onClose,
}: {
  data: MinisearchData
  onClose: () => void
}) => {
  const { t } = useTranslation('product')
  const [inputValue, setInputValue] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [isSearchingNow, setIsSearchingNow] = useState(false)
  const [showSkeletons, setShowSkeletons] = useState(false)
  const [initialRefinements, setInitialRefinements] = useState<
    { label: string; value: string; count: number; attribute: string }[]
  >([])
  const { items } = useHits<FormattedVariantProps>()

  const { query, refine, clear } = useSearchBox()
  const debouncedSearch = useMemo(
    () =>
      funnel(
        (latestValue: string) => {
          if (!latestValue) {
            return
          }

          setHasSearched(true)
          refine(latestValue)
        },
        {
          minQuietPeriodMs: 300,
          reducer: (_, latestValue: string) => latestValue,
        },
      ),
    [refine],
  )

  const { items: typeRefinements } = useRefinementList({
    attribute: 'product_type_filter',
    limit: 5,
  })

  const { items: colorRefinements } = useRefinementList({
    attribute: 'colors_filter',
    limit: 5,
  })

  const { items: collectionRefinements } = useRefinementList({
    attribute: 'collections.handle',
    limit: 5,
  })

  // Store initial refinements when there's no query
  useEffect(() => {
    if (!query && !isEmpty(typeRefinements) && !isEmpty(colorRefinements)) {
      const typeRefinementsWithAttribute = typeRefinements.map((item) => ({
        ...item,
        attribute: 'product_type_filter',
      }))
      const colorRefinementsWithAttribute = colorRefinements.map((item) => ({
        ...item,
        attribute: 'colors_filter',
      }))

      const combined = [
        ...typeRefinementsWithAttribute,
        ...colorRefinementsWithAttribute,
      ].sort((a, b) => b.count - a.count)

      if (!isEmpty(combined)) {
        setInitialRefinements(combined.slice(0, 8))
      }
    }
  }, [query, typeRefinements, colorRefinements])

  const refinements = useMemo(() => {
    const typeRefinementsWithAttribute = typeRefinements.map((item) => ({
      ...item,
      attribute: 'product_type_filter',
    }))
    const colorRefinementsWithAttribute = colorRefinements.map((item) => ({
      ...item,
      attribute: 'colors_filter',
    }))

    const combined = [
      ...typeRefinementsWithAttribute,
      ...colorRefinementsWithAttribute,
    ].sort((a, b) => b.count - a.count)

    // Use initial refinements when query exists but no results found
    if (query && isEmpty(items) && !isEmpty(initialRefinements)) {
      return initialRefinements
    }

    return query && !isEmpty(items) ? combined : combined.slice(0, 8)
  }, [typeRefinements, colorRefinements, query, items, initialRefinements])

  const { recentProducts, loading } = useRecentProducts()

  // Generate the search URL using the utility function
  const searchUrl = useMemo(
    () => generateSearchUrl(inputValue.trim() || query),
    [inputValue, query],
  )

  // Track when a search is initiated to prevent flashing
  useEffect(() => {
    if (query) {
      setHasSearched(true)
      setIsSearchingNow(false) // Query has updated, search is complete
      setShowSkeletons(true)

      // Keep skeletons visible longer to prevent flash when categories are loading
      const timer = setTimeout(() => {
        setShowSkeletons(false)
      }, 800) // Increased timeout to allow categories to load

      return () => {
        clearTimeout(timer)
      }
    }

    setHasSearched(false)
    setIsSearchingNow(false)
    setShowSkeletons(false)

    return undefined
  }, [query])

  // Also hide skeletons when we have both products and some category refinements
  useEffect(() => {
    if (query && !isEmpty(items) && !isEmpty(collectionRefinements)) {
      // Wait a bit longer before hiding to ensure categories start loading
      const timer = setTimeout(() => {
        setShowSkeletons(false)
      }, 500)

      return () => {
        clearTimeout(timer)
      }
    }

    return undefined
  }, [query, items, collectionRefinements])

  const shouldShowDefaultContent = !query && !hasSearched && !isSearchingNow

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!inputValue) {
      debouncedSearch.cancel()
      clear()
    }
  }, [inputValue, clear, debouncedSearch])

  useEffect(
    () => () => {
      debouncedSearch.cancel()
    },
    [debouncedSearch],
  )

  // Handle ESC key to close the search
  const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose()
    }
  }

  useEventListener('keydown', handleEscKey)

  const handleSearchSubmit = () => {
    const sanitizedInput = inputValue.trim() || query?.trim() || ''
    const targetUrl = generateSearchUrl(sanitizedInput)

    if (isEmpty(sanitizedInput)) {
      return
    }

    if (!debouncedSearch.isIdle) {
      debouncedSearch.flush()
    }

    window.location.assign(targetUrl)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const sanitizedValue = value.trim()

    setInputValue(value)

    if (isEmpty(sanitizedValue)) {
      debouncedSearch.cancel()
      clear()
      setHasSearched(false)
      setIsSearchingNow(false)
      setShowSkeletons(false)

      return
    }

    setIsSearchingNow(true)
    debouncedSearch.call(sanitizedValue)
  }

  return (
    <S.MinisearchContent>
      <S.InputContainer role="search">
        <SearchIcon aria-hidden="true" />
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          type="text"
          name="search"
          placeholder={data.settings?.inputPlaceholder}
          value={inputValue}
          aria-label={data.settings?.inputPlaceholder || 'Search products'}
          aria-describedby="search-instructions"
          autoComplete="off"
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearchSubmit()
            }
          }}
        />
        <div id="search-instructions" className="sr-only">
          Type to search products, press Enter or click arrow to search
        </div>
        {!isEmpty(inputValue) && (
          <button
            type="button"
            aria-label="Perform search"
            onClick={handleSearchSubmit}
          >
            <ArrowIcon aria-hidden="true" />
          </button>
        )}
        {isEmpty(inputValue) && (
          <button
            type="button"
            className="fixed-width"
            aria-label="Close search"
            onClick={onClose}
          >
            <CloseIcon aria-hidden="true" />
          </button>
        )}
      </S.InputContainer>
      {query && isEmpty(items) && (
        <S.NoResults role="status" aria-live="polite">
          <S.NoResultsTitle>{data.settings?.noResultsTitle}</S.NoResultsTitle>
          <S.NoResultDescription>
            {data.settings?.noResultsDescription} &quot;{query}&quot;
          </S.NoResultDescription>
        </S.NoResults>
      )}
      <S.Results>
        <S.SideResults>
          {query && (isSearchingNow || showSkeletons) && (
            <SuggestionsSkeleton />
          )}
          {query &&
            !isEmpty(items) &&
            !isEmpty(refinements) &&
            !isSearchingNow &&
            !showSkeletons && (
              <S.SuggestionsContainer>
                <h2>{data.settings?.maybeLookingForLabel}</h2>
                <S.VerticalList role="list" data-testid="suggested-refinements">
                  {React.Children.toArray(
                    refinements.map((item) => (
                      <S.FilterChip
                        as="a"
                        role="listitem"
                        aria-label={`Filter by ${item.value}`}
                        href={generateSearchUrl(query, [
                          {
                            attribute: item.attribute,
                            value: item.value,
                          } as Refinement,
                        ])}
                      >
                        {item.value}
                      </S.FilterChip>
                    )),
                  )}
                </S.VerticalList>
              </S.SuggestionsContainer>
            )}
          {query &&
            isEmpty(items) &&
            !isEmpty(refinements) &&
            !isSearchingNow &&
            !showSkeletons && (
              <S.SuggestionsContainer>
                <h2>{data.settings?.mostSearchedLabel}</h2>
                <S.VerticalList role="list">
                  {refinements.map((item) => (
                    <S.FilterChip
                      key={item.label}
                      as="a"
                      role="listitem"
                      aria-label={`Search for ${item.value}`}
                      href={generateSearchUrl('', [
                        {
                          attribute: item.attribute,
                          value: item.value,
                        } as Refinement,
                      ])}
                    >
                      {item.value}
                    </S.FilterChip>
                  ))}
                </S.VerticalList>
              </S.SuggestionsContainer>
            )}
          {shouldShowDefaultContent && !isEmpty(refinements) && (
            <>
              {data.editorialLinks && !isEmpty(data.editorialLinks.links) && (
                <S.SuggestionsContainer>
                  <h2>{data.editorialLinks.title}</h2>
                  <S.VerticalList role="list">
                    {data.editorialLinks.links.map((link, linkIndex) => (
                      <S.FilterChip
                        key={linkIndex}
                        as="a"
                        role="listitem"
                        href={link.url}
                      >
                        {link.title}
                      </S.FilterChip>
                    ))}
                  </S.VerticalList>
                </S.SuggestionsContainer>
              )}
              <S.SuggestionsContainer>
                <h2>{data.settings?.mostSearchedLabel}</h2>
                <S.VerticalList
                  role="list"
                  data-testid="most-searched-refinements"
                >
                  {refinements.map((item) => (
                    <S.FilterChip
                      key={item.label}
                      as="a"
                      role="listitem"
                      aria-label={`Search for ${item.value}`}
                      href={generateSearchUrl('', [
                        {
                          attribute: item.attribute,
                          value: item.value,
                        } as Refinement,
                      ])}
                    >
                      {item.value}
                    </S.FilterChip>
                  ))}
                </S.VerticalList>
              </S.SuggestionsContainer>
            </>
          )}
        </S.SideResults>
        <S.MainResult>
          {query && (isSearchingNow || showSkeletons) && (
            <>
              <CollectionSearchResultsSkeleton />
              <ProductSearchResultsSkeleton showCollections />
            </>
          )}
          {query &&
            !isEmpty(items) &&
            !isEmpty(collectionRefinements) &&
            !isSearchingNow &&
            !showSkeletons && (
              <S.SuggestionsContainer>
                <h2>{data.settings?.suggestedCategoriesLabel}</h2>
                <S.HorizontalList
                  role="region"
                  tabIndex={0}
                  aria-label="Suggested categories - swipe to see more"
                >
                  {collectionRefinements.map((item) => (
                    <CategoryTile key={item.label} handle={item.value} />
                  ))}
                </S.HorizontalList>
              </S.SuggestionsContainer>
            )}
          {query && !isEmpty(items) && !isSearchingNow && !showSkeletons && (
            <S.SuggestionsContainer>
              <S.TitleContainer>
                <h2>{data.settings?.productsLabel}</h2>
                <S.ViewAllLink href={searchUrl}>
                  {data.settings?.viewAllLabel}
                </S.ViewAllLink>
              </S.TitleContainer>
              <S.HorizontalList
                role="region"
                tabIndex={0}
                aria-label="Search results - swipe to see more products"
              >
                {items
                  .slice(0, !isEmpty(collectionRefinements) ? 4 : 8)
                  .map((item) => (
                    <ApparelProduct
                      showProductType
                      enableTouchImageSwipe
                      layout="search"
                      key={item.objectID}
                      {...formatAlgoliaHit(item)}
                    />
                  ))}
              </S.HorizontalList>
            </S.SuggestionsContainer>
          )}
          {(shouldShowDefaultContent || (query && isEmpty(items))) &&
            data.editorialCards?.cards &&
            !isEmpty(data.editorialCards.cards) && (
              <S.SuggestionsContainer className="main">
                <S.TitleContainer>
                  <h2>{data.editorialCards.title}</h2>
                </S.TitleContainer>
                <S.CardsContainer
                  role="region"
                  tabIndex={0}
                  aria-label="Editorial content - swipe to see more"
                >
                  {data.editorialCards.cards.map((card, cardIndex) => (
                    <S.Card key={cardIndex}>
                      <S.CardLink
                        href={card.link}
                        aria-label={`View ${card.text}`}
                      >
                        <S.CardImage
                          lqip
                          lazyload
                          alt={card.text}
                          sizes="(min-width: 768px) 40vw, 90vw"
                          imgSrc={setQueryParams(card.image, [
                            { name: 'width', value: '30' },
                          ])}
                          sources={buildIntrinsicWidthSources({
                            src: card.image,
                          })}
                        />
                      </S.CardLink>
                      <S.CardLink href={card.link}>{card.text}</S.CardLink>
                    </S.Card>
                  ))}
                </S.CardsContainer>
              </S.SuggestionsContainer>
            )}
          {shouldShowDefaultContent &&
            data.editorialCards?.cards &&
            isEmpty(data.editorialCards.cards) && <EditorialCardsSkeleton />}
          {(shouldShowDefaultContent || (query && isEmpty(items))) && (
            <S.SuggestionsContainer className="main">
              <S.TitleContainer>
                <h2>{t('recentProductsTitle')}</h2>
              </S.TitleContainer>
              <S.HorizontalList
                role="region"
                tabIndex={0}
                aria-label="Recently viewed products - swipe to see more"
              >
                {loading
                  ? Array.from({ length: 4 }).map((_, index) => (
                      <ApparelProductSkeleton
                        showProductType
                        key={index}
                        layout="card"
                      />
                    ))
                  : recentProducts
                      .slice(0, 4)
                      .map((item) => (
                        <ApparelProduct
                          showProductType
                          enableTouchImageSwipe
                          layout="search"
                          key={item.id}
                          {...item}
                          loading={loading}
                        />
                      ))}
              </S.HorizontalList>
            </S.SuggestionsContainer>
          )}
        </S.MainResult>
      </S.Results>
    </S.MinisearchContent>
  )
}
